import {
  AnySectionParams,
  ConsumptionRateSectionParams,
  FinancialOverallSectionParams,
  ImpactTrackingSectionParams,
  ImplementationTrackingSectionParams,
  OrganizationImpactSectionParams,
  ProgramImpactSectionParams,
  ProjectImpactSectionParams,
} from "@hpo/client/models/Archive";
import smoothJoin from "@hpo/client/utilities/smoothJoin";

export type SectionDescriptor<Section extends AnySectionParams> = {
  type: Section["type"];
  title: string;
  description: string;
  helpers: (s: Section) => Array<string>;
};

export const FinancialOverallSectionDescriptor: SectionDescriptor<FinancialOverallSectionParams> =
  {
    type: "financial-overall",
    title: "💰 Bilan financier global",
    description:
      "Tableaux reprennant les statistiques financières globales, par appel à projets et par bénéficiare",
    helpers: (s) => [
      `💰 Bilan financier global`,
      `Tableaux`,
      `📅 Périodes ${smoothJoin(s.periods)}`,
    ],
  };

export const ImpactTrackingSectionDescriptor: SectionDescriptor<ImpactTrackingSectionParams> =
  {
    type: "impact-tracking",
    title: "⚡ Impact",
    description:
      "Tableaux reprennant les étiquettes d'impact, par appel à projets et par bénéficiare",
    helpers: (s) => [
      `⚡ Impact`,
      `Tableaux`,
      `📅 Périodes ${smoothJoin(s.periods)}`,
    ],
  };

export const ImplementationTrackingSectionDescriptor: SectionDescriptor<ImplementationTrackingSectionParams> =
  {
    type: "implementation-tracking",
    title: "☑️ Implémentation des projets",
    description:
      "Tableaux reprennant les indicateurs d'évaluation saisis par les bénéficaires, pour chaque projet",
    helpers: (s) => [
      `☑️ Implémentation des projets`,
      `Tableaux`,
      `📅 Périodes ${smoothJoin(s.periods)}`,
    ],
  };

export const ConsumptionRateSectionDescriptor: SectionDescriptor<ConsumptionRateSectionParams> =
  {
    type: "consumption-rate",
    title: "📈 💰 Evolution des taux de consommation",
    description:
      "Graphiques représentant l'évolution des taux de consommation dans le temps. Affiche un graphique par appel à projet, avec une ligne pour l'appel à projet et une ligne par bénéficiaire",
    helpers: (s) => [
      `💰 Evolution des taux de consommation`,
      `📈 Graphiques`,
      `📅 Périodes ${smoothJoin(s.periods)}`,
    ],
  };
export const ProgramImpactSectionDescriptor: SectionDescriptor<ProgramImpactSectionParams> =
  {
    type: "program-impact",
    title: "📈 ⚡ Impact des appels à projets",
    description:
      "Graphique représentant l'impact des appels à projets, pour une étiquette donnée. Affiche un seul graphique, reprenant les valeurs déclarées par les bénéficiaires (réalisé) sur les d'indicateurs d'impacts d'une étiquette donnée, détaillées pour certains appels à projet",
    helpers: (s) => [
      `⚡ Impact des appels à projets`,
      `📈 Graphique`,
      `📅 Périodes ${smoothJoin(s.periods)}`,
      `${s.programs.length} appel(s) à projets`,
    ],
  };
export const ProjectImpactSectionDescriptor: SectionDescriptor<ProjectImpactSectionParams> =
  {
    type: "project-impact",
    title: "📈 ⚡ Impact des projets",
    description:
      "Graphique représentant l'impact des projets, pour une étiquette donnée. Affiche un seul graphique, reprenant les valeurs déclarées par les bénéficiaires (réalisé) sur les d'indicateurs d'impacts d'une étiquette donnée, détaillées pour certains projets",
    helpers: (s) => [
      `⚡ Impact des projets`,
      `📈 Graphique`,
      `📅 Périodes ${smoothJoin(s.periods)}`,
      `${s.projects.length} projets`,
    ],
  };

export const OrganizationImpactSectionDescriptor: SectionDescriptor<OrganizationImpactSectionParams> =
  {
    type: "organization-impact",
    title: "📈 ⚡ Impact des bénéficiaires",
    description:
      "Graphique représentant l'impact des bénéficiaires, pour une étiquette donnée. Affiche un seul graphique, reprenant les valeurs déclarées par les bénéficiaires (réalisé) sur les d'indicateurs d'impacts d'une étiquette donnée, détaillées pour certains bénéficiaires",
    helpers: (s) => [
      `⚡ Impact des bénéficiaires`,
      `📈 Graphique`,
      `📅 Périodes ${smoothJoin(s.periods)}`,
      `${s.orgs.length} bénéficiaire(s)`,
    ],
  };

export const sectionDescriptors = [
  FinancialOverallSectionDescriptor,
  ImpactTrackingSectionDescriptor,
  ImplementationTrackingSectionDescriptor,
  ConsumptionRateSectionDescriptor,
  ProgramImpactSectionDescriptor,
  ProjectImpactSectionDescriptor,
  OrganizationImpactSectionDescriptor,
];
